import { IonIcon, IonTextarea, useIonRouter } from "@ionic/react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  calendarOutline,
  clipboardOutline,
  closeOutline,
  copyOutline,
  ellipsisHorizontal,
  flagOutline,
  helpCircleOutline,
  hourglassOutline,
  locationOutline,
  logoWhatsapp,
  shareOutline,
  timeOutline,
} from "ionicons/icons";
import { toast } from "sonner";
import { useQRScanner } from "@/components/global/qr-scanner/hooks/use-qr-scanner";
import { ModalSheetAutoHeight } from "@/components/modal/sheet/auto-height";
import { ProjectImage } from "@/components/molecules/project/project-image";
import { HoursFormModal } from "@/components/molecules/shift/hours-form-modal";
import { useBoomerForm } from "@/form/boomer/hook";
import { useValidator } from "@/form/common/validator";
import { api } from "@/lib/backend";
import { type Shift } from "@/lib/backend/api";
import { extractionWrapper } from "@/lib/backend/utils/extract";
import { useFeatureFlags } from "@/lib/backend/utils/hooks/use-feature-flags";
import { Share } from "@/lib/capacitor/share";
import { MINIMUM_TIME_BEFORE_CLOCK_IN_MINUTES } from "@/lib/constants";
import { useHoursInfoSections } from "@/lib/constants/application-info";
import { ExportIcon } from "@/lib/icons/export.icon";
import { timeIcon } from "@/lib/icons/time.icon";
import { Routes } from "@/lib/router";
import { openFile } from "@/lib/utils/download";
import { openExternalUrl } from "@/lib/utils/open-url";
import { logger } from "@/logger";
import { formatInTz, formatTime, formatTimeRange } from "@/utils/format-datetime";
import {
  durationToMinutes,
  formatDurationToTimeString,
  minutesToDuration,
  parseTimeString,
} from "@/utils/time";

type ShiftCardOverviewProps = {
  shift: Shift;
};

export function UpcomingShiftCardOverview({ shift }: ShiftCardOverviewProps) {
  const { t } = useTranslation();
  const hoursInfoSections = useHoursInfoSections();
  const router = useIonRouter();
  const queryClient = useQueryClient();
  const [openPopover, setOpenPopover] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");
  const modalOfferShift = useRef<HTMLIonModalElement>(null);
  const modalTransferRequest = useRef<HTMLIonModalElement>(null);
  const modalRequestCancellation = useRef<HTMLIonModalElement>(null);
  const modalClockIn = useRef<HTMLIonModalElement>(null);
  const modalClockOut = useRef<HTMLIonModalElement>(null);
  const modalClockInInfo = useRef<HTMLIonModalElement>(null);
  const modalClockOutInfo = useRef<HTMLIonModalElement>(null);
  const modalHoursRef = useRef<HTMLIonModalElement>(null);
  const hoursInfoModalRef = useRef<HTMLIonModalElement>(null);

  const validator = useValidator();

  // Alles op deze kaart staat in projecttijd, nooit in die van het toestel: een dienst van
  // 15:00-23:00 las op een telefoon in Toronto als 09:00-17:00 en de datum ernaast viel een dag
  // terug. Dat geldt ook voor de voorgevulde uren in het formulier — die gaan als "HH:mm" terug
  // naar de server, die ze in projecttijd leest.
  const timeZone = shift.timeZone ?? shift.project?.timeZone;

  const { data: featureFlags } = useFeatureFlags();
  const shiftSwapEnabled = featureFlags?.shiftSwapEnabled ?? false;

  // Fetch documents for this project to get the briefing (first document)
  const { data: documents } = useQuery({
    queryKey: ["documents", shift.projectId],
    queryFn: () => extractionWrapper(api.document.getAll("project", shift.projectId!)),
    enabled: !!shift.projectId,
  });

  const briefingDocument = documents?.items?.[0];

  // Mutation for creating transfer request
  const transferMutation = useMutation({
    mutationFn: async () => {
      const response = await api.shiftCancellation.createTransferRequest({
        shiftId: shift.id,
        reason: t("shift:actions.offer-shift.action"),
      });
      return response.data;
    },
    onSuccess: async () => {
      const TOAST_ID = "offer-shift";
      toast.success(t("shift:actions.offer-shift.success"), {
        id: TOAST_ID,
        duration: 2000,
      });
      // Close the offer shift modal
      modalOfferShift.current?.dismiss();
      // Refetch queries to ensure fresh shift data before opening transfer modal
      await queryClient.refetchQueries({ queryKey: ["shifts"] });
      // Open the transfer request modal after a short delay to ensure UI is ready
      setTimeout(() => {
        modalTransferRequest.current?.present();
      }, 100);
    },
    onError: (error: any) => {
      const TOAST_ID = "offer-shift";
      const errorCode =
        error?.error?.errorCode || error?.data?.errorCode || error?.errorCode || null;
      const errorMessage = errorCode ? t(`error-codes:${errorCode}`) : undefined;
      toast.error(errorMessage || t("shift:actions.offer-shift.error"), { id: TOAST_ID });
    },
  });

  // Mutation for requesting cancellation
  const cancellationMutation = useMutation({
    mutationFn: async (reason?: string) => {
      const response = await api.shiftCancellation.requestCancellation({
        shiftId: shift.id,
        reason: reason || t("shift:actions.request-cancellation.action"),
      });
      return response.data;
    },
    onSuccess: () => {
      const TOAST_ID = "request-cancellation";
      toast.success(t("shift:actions.request-cancellation.success"), { id: TOAST_ID });
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
    },
    onError: (error: any) => {
      const TOAST_ID = "request-cancellation";
      const errorCode =
        error?.error?.errorCode || error?.data?.errorCode || error?.errorCode || null;
      const errorMessage = errorCode ? t(`error-codes:${errorCode}`) : undefined;
      toast.error(errorMessage || t("shift:actions.request-cancellation.error"), { id: TOAST_ID });
    },
  });

  // QR Clock In Mutation
  const qrClockInMutation = useMutation({
    mutationKey: ["shift", shift.id, "qr-clock-in"],
    mutationFn: async (qrCodeData: string) => {
      if (!shift.project?.id) {
        throw new Error("Project ID is required");
      }
      return await extractionWrapper(
        api.shiftDeclaration.clockIn({
          projectId: shift.project.id,
          qrCodeData,
          shiftId: shift.id,
        }),
      );
    },
    onMutate: () => {
      // Geen toast.loading — zie `sonner.guard.test.ts`. De knop toont de voortgang al.
      toast.dismiss(`shift-${shift.id}-clock`);
    },
    onSuccess: async () => {
      toast.success(t("shift:clock-in.success"), {
        id: `shift-${shift.id}-clock`,
      });
      await queryClient.refetchQueries({ queryKey: ["shifts"] });
      modalClockIn.current?.dismiss();
    },
    onError: (error: any) => {
      const message = error?.message || t("shift:clock-in.error");
      toast.error(message, { id: `shift-${shift.id}-clock` });
      logger.error("QR clock-in error", error);
    },
  });

  const handleDownload = async (fileName: string) => {
    try {
      // Properly encode the document name for use in the URL path
      const encodedFileName = encodeURIComponent(fileName);
      const response = await api.file.getProjectDocumentUrl(shift.projectId!, encodedFileName);
      openFile(response.data.url);
    } catch (error) {
      logger.error("Failed to download briefing:", error);
      toast.error(t("shift:actions.briefing-error"));
    }
  };

  // QR Clock Out Mutation
  const qrClockOutMutation = useMutation({
    mutationKey: ["shift", shift.id, "qr-clock-out"],
    mutationFn: async (qrCodeData: string) => {
      if (!shift.project?.id) {
        throw new Error("Project ID is required");
      }
      return await extractionWrapper(
        api.shiftDeclaration.clockOut({
          projectId: shift.project.id,
          qrCodeData,
          shiftId: shift.id,
        }),
      );
    },
    onMutate: () => {
      // Geen toast.loading — zie `sonner.guard.test.ts`. De knop toont de voortgang al.
      toast.dismiss(`shift-${shift.id}-clock`);
    },
    onSuccess: async () => {
      toast.success(t("shift:clock-out.success"), {
        id: `shift-${shift.id}-clock`,
      });
      await queryClient.refetchQueries({ queryKey: ["shifts"] });
      modalClockOut.current?.dismiss();
    },
    onError: (error: any) => {
      const message = error?.message || t("shift:clock-out.error");
      toast.error(message, { id: `shift-${shift.id}-clock` });
      logger.error("QR clock-out error", error);
    },
  });

  // QR Scanner hooks
  const {
    scanQRCode: scanClockIn,
    isScanning: isScanningClockIn,
    WebScannerComponent: ClockInWebScanner,
  } = useQRScanner({
    onScanSuccess: (data) => qrClockInMutation.mutate(data),
  });

  const {
    scanQRCode: scanClockOut,
    isScanning: isScanningClockOut,
    WebScannerComponent: ClockOutWebScanner,
  } = useQRScanner({
    onScanSuccess: (data) => qrClockOutMutation.mutate(data),
  });

  const handleCardClick = () => {
    if (shift.projectId) {
      router.push(
        Routes.Project({ id: shift.projectId }, { search: { origin: "upcoming" } }),
        "forward",
      );
    }
  };

  const handleViewAvailability = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenPopover(false);
    // Navigate to project detail to see availability
    if (shift.projectId) {
      router.push(
        Routes.Project({ id: shift.projectId }, { search: { origin: "upcoming" } }),
        "forward",
      );
    }
  };

  const handleOfferShift = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenPopover(false);
    setTimeout(() => {
      modalOfferShift.current?.present();
    }, 100);
  };

  const handleConfirmOfferShift = async () => {
    const TOAST_ID = "offer-shift";
    // Geen toast.loading — zie `sonner.guard.test.ts`. De bevestigknop hieronder is
    // uitgeschakeld en verandert van tekst zolang de mutatie loopt.
    toast.dismiss(TOAST_ID);
    try {
      await transferMutation.mutateAsync();
      // Modal dismissal and transfer modal opening are handled in mutation's onSuccess
    } catch (error) {
      // Error is already handled by mutation's onError
    }
  };

  const handleRequestCancellation = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenPopover(false);
    setTimeout(() => {
      modalRequestCancellation.current?.present();
    }, 100);
  };

  const handleConfirmRequestCancellation = async () => {
    const TOAST_ID = "request-cancellation";
    // Idem — de bevestigknop toont de voortgang al.
    toast.dismiss(TOAST_ID);
    try {
      await cancellationMutation.mutateAsync(cancellationReason.trim() || undefined);
      setCancellationReason("");
      modalRequestCancellation.current?.dismiss();
    } catch (error) {
      // Error is already handled by mutation's onError
    }
  };

  const handleClockIn = (e: React.MouseEvent) => {
    e.stopPropagation();
    modalClockIn.current?.present();
  };

  const handleConfirmClockIn = () => {
    scanClockIn();
  };

  const handleClockOut = (e: React.MouseEvent) => {
    e.stopPropagation();
    modalClockOut.current?.present();
  };

  const handleConfirmClockOut = () => {
    scanClockOut();
  };

  const handleViewTransferRequest = (e: React.MouseEvent) => {
    e.stopPropagation();
    modalTransferRequest.current?.present();
  };

  const handleCopyTransferLink = async () => {
    const transferUrl = shift.stats?.transferRequestUrl;
    if (transferUrl) {
      try {
        await navigator.clipboard.writeText(transferUrl);
        toast.success(t("shift:transfer.link-copied"));
      } catch (error) {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = transferUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        toast.success(t("shift:transfer.link-copied"));
      }
    }
  };

  const handleShareTransferLink = async () => {
    const transferUrl = shift.stats?.transferRequestUrl;
    if (!transferUrl) {
      return;
    }

    try {
      const canShareResult = await Share.canShare();
      if (canShareResult) {
        await Share.share({
          title: shift.project?.name || "Shift",
          text: t("shift:transfer.share-text"),
          url: transferUrl,
        });
      } else {
        toast.error(t("shift:transfer.share-unsupported"));
      }
    } catch (err) {
      logger.error("Share failed:", err);
    }
  };

  // Hours registration mutation for manual entry
  const hoursMutation = useMutation({
    mutationKey: ["shift", shift.id, "hours"],
    mutationFn: async (variables: { startTime: string; endTime: string; breakTime: number }) => {
      await api.shiftDeclaration.updateFullEntry(shift.id, {
        startAt: variables.startTime,
        endAt: variables.endTime,
        breakTime: variables.breakTime,
        shiftId: shift.id,
      });
    },
    onMutate: () => {
      // Geen toast.loading — zie `sonner.guard.test.ts`. De knop toont de voortgang al.
      toast.dismiss(`shift-${shift.id}-hours`);
    },
    onSuccess: () => {
      toast.success(t("actions:declare-hours.success"), {
        id: `shift-${shift.id}-hours`,
      });
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
      queryClient.invalidateQueries({ queryKey: ["shift", shift.id] });
      setTimeout(() => {
        modalHoursRef.current?.dismiss();
      }, 100);
    },
    onError: (error) => {
      toast.error(t("actions:declare-hours.error"), {
        id: `shift-${shift.id}-hours`,
      });
      logger.error(error);
    },
  });

  // Prepare form with existing or planned times
  const existingStartAt =
    (typeof shift.shiftTimeEntry?.startAt === "string"
      ? shift.shiftTimeEntry.startAt
      : undefined) ?? shift.startAt;

  const existingEndAt =
    (typeof shift.shiftTimeEntry?.endAt === "string" ? shift.shiftTimeEntry.endAt : undefined) ??
    shift.endAt;

  const [formHours, { Element: FormElementHours }] = useBoomerForm({
    defaultValues: {
      startTime: existingStartAt ? formatTime(existingStartAt, { timeZone }) : "",
      endTime: existingEndAt ? formatTime(existingEndAt, { timeZone }) : "",
      breakDuration: formatDurationToTimeString(
        minutesToDuration(shift.shiftTimeEntry?.breakTimeMinutes ?? 0),
      ),
    },
    onSubmit: async ({ value }) => {
      await hoursMutation.mutateAsync({
        ...value,
        breakTime: durationToMinutes(parseTimeString(value.breakDuration)),
      });
    },
  });

  const handleRegisterHours = (e: React.MouseEvent) => {
    e.stopPropagation();
    modalHoursRef.current?.present();
  };

  // Format month abbreviation (e.g., "Dec")
  const month = formatInTz(shift.startAt, { month: "short", timeZone });

  // Format day (e.g., "27")
  const day = formatInTz(shift.startAt, { day: "numeric", timeZone });

  // Format weekday abbreviation (e.g., "Wo")
  const weekday = formatInTz(shift.startAt, { weekday: "short", timeZone });

  // Format time range (e.g., "12:00 - 18:00")
  const timeRange = formatTimeRange(shift.startAt, shift.endAt, { timeZone });

  // Check if user can clock in (within MINIMUM_TIME_BEFORE_CLOCK_IN_MINUTES before shift start)
  const canClockIn = () => {
    const now = new Date();
    const shiftStart = new Date(shift.startAt);
    const minutesUntilStart = (shiftStart.getTime() - now.getTime()) / (1000 * 60);
    return minutesUntilStart <= MINIMUM_TIME_BEFORE_CLOCK_IN_MINUTES;
  };

  // Check if shift has started (for manual hours registration)
  const hasShiftStarted = () => {
    const now = new Date();
    const shiftStart = new Date(shift.startAt);
    return now >= shiftStart;
  };

  // Check if hours can be registered manually (for non-QR projects, after shift start)
  const canRegisterHoursManually =
    hasShiftStarted() &&
    !shift.project?.enableQRCodeClocking &&
    shift.project?.allowEmployeeSubmitHours &&
    shift.project?.status !== "FINALIZED";

  return (
    <>
      <div onClick={handleCardClick} className="flex w-full cursor-pointer items-start gap-5">
        {/* Date badge */}
        <div className="flex w-12 shrink-0 flex-col items-center px-1 py-1 capitalize">
          <div className="flex flex-col items-center pb-0.5 text-center text-txt-primary">
            <p className="text-sm font-semibold leading-[1.4]">{month}</p>
            <p className="text-2xl font-semibold leading-[1.2]">{day}</p>
          </div>
          <p className="text-sm font-medium leading-[1.4] text-txt-tertiary">{weekday}</p>
        </div>

        {/* Shift details */}
        <div className="flex flex-1 items-start gap-5 border-b border-solid border-bordercolor-primary">
          <div className="flex flex-1 flex-col gap-4 pb-5">
            {/* Project image */}
            <ProjectImage
              projectId={shift.projectId}
              imageKey={shift.project?.image}
              alt={shift.project?.name || "Project"}
              className="aspect-video w-full rounded-xl"
            />

            {/* Project name and type label */}
            <div className="flex flex-col">
              <p className="text-xs font-bold uppercase tracking-wide text-txt-secondary">DIENST</p>
              <h3 className="overflow-hidden text-ellipsis text-lg font-semibold leading-[1.6] text-txt-primary">
                {shift.project?.name}
              </h3>
            </div>

            {/* Details with icons */}
            <div className="flex flex-col gap-2">
              {/* User function */}
              {shift.userFunction?.name && (
                <div className="flex items-center gap-1.5">
                  <IonIcon icon={clipboardOutline} className="size-5 text-txt-tertiary" />
                  <p className="text-md font-medium leading-[1.5] text-txt-tertiary">
                    {shift.userFunction.name}
                  </p>
                </div>
              )}

              {/* Position */}
              {shift.position?.name && (
                <div className="flex items-center gap-1.5">
                  <IonIcon icon={flagOutline} className="size-5 text-txt-tertiary" />
                  <p className="text-md font-medium leading-[1.5] text-txt-tertiary">
                    {shift.position.name}
                  </p>
                </div>
              )}

              {/* Address */}
              {shift.project?.addressLine && (
                <div className="flex items-center gap-1.5">
                  <IonIcon icon={locationOutline} className="size-5 text-txt-tertiary" />
                  <p className="text-md font-medium leading-[1.5] text-txt-tertiary">
                    {shift.project.addressLine}
                  </p>
                </div>
              )}

              {/* Time */}
              <div className="flex items-center gap-1.5">
                <IonIcon icon={timeOutline} className="size-5 text-txt-tertiary" />
                <p className="text-md font-medium leading-[1.5] text-txt-tertiary">{timeRange}</p>
              </div>

              {/* Cancellation request pending status */}
              {shift.stats?.hasPendingCancellation && (
                <div className="flex items-center gap-1.5">
                  <IonIcon icon={hourglassOutline} className="size-5 text-warning-120" />
                  <p className="text-md font-medium leading-[1.5] text-warning-120">
                    {t("shift:status.cancellation-pending")}
                  </p>
                </div>
              )}

              {/* Transfer request pending status - clickable to view/share */}
              {shiftSwapEnabled &&
                shift.stats?.hasPendingTransfer &&
                !shift.stats?.hasPendingCancellation && (
                  <button
                    type="button"
                    onClick={handleViewTransferRequest}
                    className="flex items-center gap-1.5 transition-opacity active:opacity-70"
                  >
                    <ExportIcon className="size-5 text-warning-120" />
                    <span className="text-md font-medium leading-[1.5] text-warning-120 underline">
                      {t("shift:status.transfer-pending")}
                    </span>
                  </button>
                )}
            </div>

            {/* Action buttons */}
            <div className="flex w-full flex-col gap-2">
              {/* Briefing button - shows if there's at least one document */}
              {briefingDocument && (
                <button
                  type="button"
                  onClick={async (e) => {
                    e.stopPropagation();
                    handleDownload(briefingDocument.name);
                  }}
                  className="flex w-full items-center justify-center gap-1.5 rounded-xl border-2 border-solid border-button-primary bg-button-primary px-4 py-3"
                >
                  <p className="text-md font-semibold leading-[1.5] tracking-[0.08px] text-txt-inverted">
                    {t("shift:actions.briefing")}
                  </p>
                </button>
              )}

              {/* Route button */}
              {shift.project?.addressLine && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const address = encodeURIComponent(shift.project?.addressLine || "");
                    window.open(
                      `https://www.google.com/maps/search/?api=1&query=${address}`,
                      "_blank",
                    );
                  }}
                  className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-solid border-bordercolor-primary px-4 py-3"
                >
                  <p className="text-md font-semibold leading-[1.5] tracking-[0.08px] text-txt-primary">
                    {t("shift:actions.view-route")}
                  </p>
                </button>
              )}

              {/* Clock In / Clock Out buttons - only show if QR clocking is enabled */}
              {shift.project?.enableQRCodeClocking && (
                <>
                  {/* Clock In button - only show if not clocked in yet and within time window */}
                  {!shift.shiftTimeEntry?.startAt && canClockIn() && (
                    <button
                      type="button"
                      onClick={handleClockIn}
                      className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-brand-500 px-4 py-3"
                    >
                      <p className="text-md font-semibold leading-[1.5] tracking-[0.08px] text-white">
                        {t("shift:clock-in.action")}
                      </p>
                      <IonIcon icon={timeIcon} className="size-4 text-white" />
                    </button>
                  )}

                  {/* Clock Out button - only show if clocked in but not clocked out */}
                  {shift.shiftTimeEntry?.startAt && !shift.shiftTimeEntry?.endAt && (
                    <button
                      type="button"
                      onClick={handleClockOut}
                      className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-brand-500 px-4 py-3"
                    >
                      <p className="text-md font-semibold leading-[1.5] tracking-[0.08px] text-white">
                        {t("shift:clock-out.action")}
                      </p>
                      <IonIcon icon={timeIcon} className="size-4 text-white" />
                    </button>
                  )}
                </>
              )}

              {/* Manual hours registration button - only for non-QR projects after shift starts */}
              {canRegisterHoursManually && (
                <button
                  type="button"
                  onClick={handleRegisterHours}
                  className="flex w-full items-center justify-center gap-1.5 rounded-xl border-2 border-solid border-button-primary bg-button-primary px-4 py-3"
                >
                  <p className="text-md font-semibold leading-[1.5] tracking-[0.08px] text-txt-inverted">
                    {shift.shiftTimeEntry?.id ? t("shift:hours.edit") : t("shift:hours.title")}
                  </p>
                  <IonIcon icon={timeIcon} className="size-4 text-txt-inverted" />
                </button>
              )}
            </div>
          </div>

          {/* Three dots menu with popover */}
          <div className="relative flex shrink-0 items-start py-2">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setOpenPopover(!openPopover);
              }}
              className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-surface-secondary"
            >
              <IonIcon icon={ellipsisHorizontal} className="text-2xl text-txt-primary" />
            </button>

            {/* Popover */}
            {openPopover && (
              <>
                {/* Backdrop to close popover when clicking outside */}
                <div
                  className="fixed inset-0 z-10"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenPopover(false);
                  }}
                />

                {/* Popover content */}
                <div className="absolute right-0 top-12 z-20 w-[293px] overflow-hidden rounded-xl border border-bordercolor-primary bg-white shadow-[0px_20px_33px_8px_rgba(0,0,0,0.08),0px_10px_18px_8px_rgba(0,0,0,0.02)]">
                  {/* WhatsApp groep - only when the project has a group link */}
                  {shift.project?.whatsappGroupLink && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenPopover(false);
                        openExternalUrl(shift.project?.whatsappGroupLink || "");
                      }}
                      className="flex h-[52px] w-full items-center justify-between border-b border-bordercolor-primary px-5 transition-colors hover:bg-surface-secondary"
                    >
                      <span className="text-md font-medium text-txt-primary">
                        {t("shift:actions.whatsapp-group")}
                      </span>
                      <IonIcon icon={logoWhatsapp} className="size-5 text-txt-primary" />
                    </button>
                  )}

                  {/* Beschikbaarheid bekijken */}
                  <button
                    type="button"
                    onClick={handleViewAvailability}
                    className="flex h-[52px] w-full items-center justify-between border-b border-bordercolor-primary px-5 transition-colors hover:bg-surface-secondary"
                  >
                    <span className="text-md font-medium text-txt-primary">
                      {t("shift:actions.view-availability")}
                    </span>
                    <IonIcon icon={calendarOutline} className="size-5 text-txt-primary" />
                  </button>

                  {/* Shift aanbieden - hide if there's already a pending cancellation or transfer */}
                  {shiftSwapEnabled &&
                    !shift.stats?.hasPendingCancellation &&
                    !shift.stats?.hasPendingTransfer && (
                      <button
                        type="button"
                        onClick={handleOfferShift}
                        className="flex h-[52px] w-full items-center justify-between border-b border-bordercolor-primary px-5 transition-colors hover:bg-surface-secondary"
                      >
                        <span className="text-md font-medium text-txt-primary">
                          {t("shift:actions.offer-shift.action")}
                        </span>
                        <IonIcon icon={shareOutline} className="size-5 text-txt-primary" />
                      </button>
                    )}

                  {/* View transfer request - show if there's an active transfer */}
                  {shiftSwapEnabled && shift.stats?.hasPendingTransfer && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenPopover(false);
                        setTimeout(() => {
                          modalTransferRequest.current?.present();
                        }, 100);
                      }}
                      className="flex h-[52px] w-full items-center justify-between border-b border-bordercolor-primary px-5 transition-colors hover:bg-surface-secondary"
                    >
                      <span className="text-md font-medium text-txt-brand">
                        {t("shift:transfer.view")}
                      </span>
                      <IonIcon icon={shareOutline} className="size-5 text-txt-brand" />
                    </button>
                  )}

                  {/* Afmeldverzoek indienen - hide if there's already a pending cancellation */}
                  {!shift.stats?.hasPendingCancellation && (
                    <button
                      type="button"
                      onClick={handleRequestCancellation}
                      className="flex h-[52px] w-full items-center justify-between px-5 transition-colors hover:bg-surface-secondary"
                    >
                      <span className="text-md font-medium text-error-120">
                        {t("shift:actions.request-cancellation.action")}
                      </span>
                      <IonIcon icon={closeOutline} className="size-5 text-error-120" />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modal for Offer Shift */}
      {shiftSwapEnabled && (
        <ModalSheetAutoHeight ref={modalOfferShift}>
          <div className="flex flex-col gap-6 p-6">
            {/* Header */}
            <div className="flex flex-col">
              <h2 className="text-xl font-bold text-txt-primary">
                {t("shift:actions.offer-shift.confirm.title")}
              </h2>
            </div>

            {/* Content */}
            <p className="text-sm leading-relaxed text-txt-secondary">
              {t("shift:actions.offer-shift.confirm.description")}
            </p>

            {/* Action button */}
            <button
              type="button"
              onClick={handleConfirmOfferShift}
              disabled={transferMutation.isPending}
              className="w-full rounded-lg border border-solid border-bordercolor-primary bg-button-primary py-3 text-center text-base font-semibold text-txt-inverted disabled:opacity-50"
            >
              {transferMutation.isPending
                ? t("shift:actions.offer-shift.loading")
                : t("shift:actions.offer-shift.confirm.button")}
            </button>
          </div>
        </ModalSheetAutoHeight>
      )}

      {/* Transfer Request View Modal */}
      {shiftSwapEnabled && (
        <ModalSheetAutoHeight ref={modalTransferRequest}>
          <div className="flex flex-col gap-6 p-6">
            {/* Header with close button */}
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-txt-primary">{t("shift:transfer.title")}</h2>
              <button
                type="button"
                onClick={() => modalTransferRequest.current?.dismiss()}
                className="flex size-8 items-center justify-center"
              >
                <IonIcon icon={closeOutline} className="text-2xl text-txt-tertiary" />
              </button>
            </div>

            {/* Project image */}
            <ProjectImage
              projectId={shift.projectId}
              imageKey={shift.project?.image}
              alt={shift.project?.name || "Project"}
              className="aspect-[16/9] w-full rounded-xl"
            />

            {/* Project name */}
            <h3 className="text-center text-2xl font-bold text-txt-primary">
              {shift.project?.name}
            </h3>

            {/* Shift details */}
            <div className="flex flex-col items-center gap-1 text-center">
              <p className="text-md text-txt-secondary">
                {formatInTz(shift.startAt, {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                  timeZone,
                })}
                , {timeRange}
              </p>
              {shift.project?.addressLine && (
                <p className="text-md text-txt-secondary">{shift.project.addressLine}</p>
              )}
              {shift.userFunction?.name && (
                <p className="text-md font-medium text-brand-500">{shift.userFunction.name}</p>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleCopyTransferLink}
                className="flex flex-1 flex-col items-center justify-center gap-2 rounded-xl border border-solid border-bordercolor-primary px-4 py-4 transition-colors active:bg-surface-secondary"
              >
                <IonIcon icon={copyOutline} className="text-2xl text-txt-primary" />
                <span className="text-sm font-medium text-txt-primary">
                  {t("shift:transfer.copy-link")}
                </span>
              </button>
              <button
                type="button"
                onClick={handleShareTransferLink}
                className="flex flex-1 flex-col items-center justify-center gap-2 rounded-xl border border-solid border-bordercolor-primary px-4 py-4 transition-colors active:bg-surface-secondary"
              >
                <IonIcon icon={shareOutline} className="text-2xl text-txt-primary" />
                <span className="text-sm font-medium text-txt-primary">
                  {t("shift:transfer.share")}
                </span>
              </button>
            </div>
          </div>
        </ModalSheetAutoHeight>
      )}

      {/* Confirmation Modal for Request Cancellation */}
      <ModalSheetAutoHeight
        ref={modalRequestCancellation}
        onDidDismiss={() => setCancellationReason("")}
      >
        <div className="flex flex-col gap-6 p-6">
          {/* Header */}
          <div className="flex flex-col">
            <h2 className="text-xl font-bold text-txt-primary">
              {t("shift:actions.request-cancellation.confirm.title")}
            </h2>
          </div>

          {/* Content */}
          <p className="text-sm leading-relaxed text-txt-secondary">
            {t("shift:actions.request-cancellation.confirm.description")}
          </p>

          {/* Cancellation reason textarea */}
          <IonTextarea
            label={t("shift:actions.request-cancellation.reason.label")}
            placeholder={t("shift:actions.request-cancellation.reason.placeholder")}
            value={cancellationReason}
            onIonInput={(e) => setCancellationReason(e.detail.value || "")}
            rows={4}
            labelPlacement="stacked"
            className="w-full"
          />

          {/* Action button */}
          <button
            type="button"
            onClick={handleConfirmRequestCancellation}
            disabled={cancellationMutation.isPending}
            className="w-full rounded-lg border border-solid border-bordercolor-primary bg-error-100 py-3 text-center text-base font-semibold text-white disabled:opacity-50"
          >
            {cancellationMutation.isPending
              ? t("shift:actions.request-cancellation.loading")
              : t("shift:actions.request-cancellation.confirm.button")}
          </button>
        </div>
      </ModalSheetAutoHeight>

      {/* Confirmation Modal for Clock In */}
      <ModalSheetAutoHeight
        ref={modalClockIn}
        onDidDismiss={() => !isScanningClockIn && modalClockIn.current?.dismiss()}
      >
        <div className="flex flex-col gap-6 p-6">
          {/* Header with info icon */}
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-txt-primary">
              {t("shift:clock-in.confirm.title")}
            </h2>
            <button
              type="button"
              onClick={() => modalClockInInfo.current?.present()}
              className="flex size-10 items-center justify-center"
            >
              <IonIcon icon={helpCircleOutline} className="text-2xl text-txt-tertiary" />
            </button>
          </div>

          {/* Explainer content */}
          <div className="flex flex-col gap-4">
            <p className="text-sm leading-relaxed text-txt-secondary">
              {t("shift:clock-in.confirm.description")}
            </p>

            <div className="flex flex-col gap-2">
              <p className="text-sm font-semibold text-txt-primary">
                {t("shift:clock-in.confirm.steps-title")}
              </p>
              <ul className="flex list-inside list-disc flex-col gap-1.5 text-sm leading-relaxed text-txt-secondary">
                <li>{t("shift:clock-in.confirm.step1")}</li>
                <li>{t("shift:clock-in.confirm.step2")}</li>
                <li>{t("shift:clock-in.confirm.step3")}</li>
                <li>{t("shift:clock-in.confirm.step4")}</li>
              </ul>
            </div>
          </div>

          {/* Action button */}
          <button
            type="button"
            onClick={handleConfirmClockIn}
            disabled={qrClockInMutation.isPending || isScanningClockIn}
            className="w-full rounded-xl bg-button-primary py-3 text-center text-base font-semibold text-txt-inverted disabled:bg-surface-tertiary disabled:text-txt-tertiary"
          >
            {qrClockInMutation.isPending || isScanningClockIn
              ? t("shift:clock-in.loading")
              : t("shift:clock-in.confirm.button")}
          </button>
        </div>
      </ModalSheetAutoHeight>

      {/* Confirmation Modal for Clock Out */}
      <ModalSheetAutoHeight
        ref={modalClockOut}
        onDidDismiss={() => !isScanningClockOut && modalClockOut.current?.dismiss()}
      >
        <div className="flex flex-col gap-6 p-6">
          {/* Header with info icon */}
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-txt-primary">
              {t("shift:clock-out.confirm.title")}
            </h2>
            <button
              type="button"
              onClick={() => modalClockOutInfo.current?.present()}
              className="flex size-10 items-center justify-center"
            >
              <IonIcon icon={helpCircleOutline} className="text-2xl text-txt-tertiary" />
            </button>
          </div>

          {/* Explainer content */}
          <div className="flex flex-col gap-4">
            <p className="text-sm leading-relaxed text-txt-secondary">
              {t("shift:clock-out.confirm.description")}
            </p>

            <div className="flex flex-col gap-2">
              <p className="text-sm font-semibold text-txt-primary">
                {t("shift:clock-out.confirm.steps-title")}
              </p>
              <ul className="flex list-inside list-disc flex-col gap-1.5 text-sm leading-relaxed text-txt-secondary">
                <li>{t("shift:clock-out.confirm.step1")}</li>
                <li>{t("shift:clock-out.confirm.step2")}</li>
                <li>{t("shift:clock-out.confirm.step3")}</li>
                <li>{t("shift:clock-out.confirm.step4")}</li>
              </ul>
            </div>
          </div>

          {/* Action button */}
          <button
            type="button"
            onClick={handleConfirmClockOut}
            disabled={qrClockOutMutation.isPending || isScanningClockOut}
            className="w-full rounded-xl bg-button-primary py-3 text-center text-base font-semibold text-txt-inverted disabled:bg-surface-tertiary disabled:text-txt-tertiary"
          >
            {qrClockOutMutation.isPending || isScanningClockOut
              ? t("shift:clock-out.loading")
              : t("shift:clock-out.confirm.button")}
          </button>
        </div>
      </ModalSheetAutoHeight>

      {/* Hours Modal for manual entry */}
      <HoursFormModal
        modalRef={modalHoursRef}
        hoursInfoModalRef={hoursInfoModalRef}
        formHours={formHours}
        FormElementHours={FormElementHours}
        validator={validator}
        closeModal={() => modalHoursRef.current?.dismiss()}
      />

      {/* QR Scanners */}
      {ClockInWebScanner && <ClockInWebScanner />}
      {ClockOutWebScanner && <ClockOutWebScanner />}

      {/* Info Modal for Clock In */}
      <ModalSheetAutoHeight ref={modalClockInInfo}>
        <div className="flex flex-col gap-6 p-6">
          <h2 className="text-xl font-bold text-txt-primary">Over uren registratie</h2>

          <div className="flex flex-col gap-4">
            {hoursInfoSections.map((section, index) => (
              <div key={index} className="flex flex-col gap-2">
                <h3 className="text-base font-semibold text-txt-primary">{section.title}</h3>
                {Array.isArray(section.content) ? (
                  <ul className="flex flex-col gap-2">
                    {section.content.map((item, i) => (
                      <li key={i} className="text-sm leading-relaxed text-txt-secondary">
                        • {item}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm leading-relaxed text-txt-secondary">{section.content}</p>
                )}
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={() => modalClockInInfo.current?.dismiss()}
            className="w-full rounded-xl bg-surface-tertiary py-3 text-center text-base font-semibold text-txt-primary"
          >
            {t("common:close")}
          </button>
        </div>
      </ModalSheetAutoHeight>

      {/* Info Modal for Clock Out */}
      <ModalSheetAutoHeight ref={modalClockOutInfo}>
        <div className="flex flex-col gap-6 p-6">
          <h2 className="text-xl font-bold text-txt-primary">Over uren registratie</h2>

          <div className="flex flex-col gap-4">
            {hoursInfoSections.map((section, index) => (
              <div key={index} className="flex flex-col gap-2">
                <h3 className="text-base font-semibold text-txt-primary">{section.title}</h3>
                {Array.isArray(section.content) ? (
                  <ul className="flex flex-col gap-2">
                    {section.content.map((item, i) => (
                      <li key={i} className="text-sm leading-relaxed text-txt-secondary">
                        • {item}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm leading-relaxed text-txt-secondary">{section.content}</p>
                )}
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={() => modalClockOutInfo.current?.dismiss()}
            className="w-full rounded-xl bg-surface-tertiary py-3 text-center text-base font-semibold text-txt-primary"
          >
            {t("common:close")}
          </button>
        </div>
      </ModalSheetAutoHeight>
    </>
  );
}
